import { markRaw } from 'vue'

import { showError } from '@/services/toastService'
import type { Photo, PhotoCoordinateSource } from '@/types/photo'

import { generateId } from './idService'

interface ProgressMessage {
  type: 'progress'
  progress: number
  message?: string
}

interface ResultMessage {
  type: 'result'
  photo: ProcessedPhoto
}

interface ErrorMessage {
  type: 'error'
  error: string
}

type WorkerMessage = ProgressMessage | ResultMessage | ErrorMessage

// Processed photo structure from worker
interface ProcessedPhoto {
  name: string
  hasCoordinates: boolean
  takenAt: number | null
  lat: number | null
  lon: number | null
  coordinateSource: PhotoCoordinateSource
  imageData: ArrayBuffer
  thumbnailData: string
  processedMimeType: string
}

// Web Worker manager for photo processing
class PhotoWorkerManager {
  private worker: Worker | null = null
  private processingCallback: ((progress: number, message?: string) => void) | null = null

  private initWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(new URL('@/workers/photoWorker.ts', import.meta.url), {
        type: 'module',
      })
    }
    return this.worker
  }

  /**
   * Process a photo ArrayBuffer using the Web Worker
   */
  async processFileBuffer(
    photoBuffer: ArrayBuffer,
    fileName: string,
    onProgress?: (progress: number, message?: string) => void,
  ): Promise<Photo> {
    return new Promise((resolve, reject) => {
      const worker = this.initWorker()
      this.processingCallback = onProgress || null

      const messageHandler = (event: MessageEvent<WorkerMessage>) => {
        const message = event.data

        switch (message.type) {
          case 'progress':
            if (this.processingCallback) {
              this.processingCallback(message.progress, message.message)
            }
            break
          case 'result':
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)
            const photo = this.convertWorkerResultToPhoto(message.photo)
            resolve(photo)
            break
          case 'error':
            worker.removeEventListener('message', messageHandler)
            worker.removeEventListener('error', errorHandler)
            reject(new Error(message.error))
            break
        }
      }

      const errorHandler = (error: ErrorEvent) => {
        worker.removeEventListener('message', messageHandler)
        worker.removeEventListener('error', errorHandler)
        reject(new Error(`Worker error: ${error.message}`))
      }

      worker.addEventListener('message', messageHandler)
      worker.addEventListener('error', errorHandler)

      // Send data to worker (transferable to avoid duplication)
      worker.postMessage(
        {
          type: 'parse',
          data: photoBuffer,
          fileName: fileName,
        },
        [photoBuffer as ArrayBuffer],
      )
    })
  }

  /**
   * Convert worker result to Photo format with proper IDs
   */
  private convertWorkerResultToPhoto(workerPhoto: ProcessedPhoto): Photo {
    // Create File object from processed image data
    const processedFile = new File([workerPhoto.imageData], workerPhoto.name, {
      type: workerPhoto.processedMimeType,
    })

    return {
      clientId: generateId(),
      name: workerPhoto.name,
      binaryData: markRaw(processedFile),
      hasCoordinates: workerPhoto.hasCoordinates,
      takenAt: workerPhoto.takenAt,
      lat: workerPhoto.lat,
      lon: workerPhoto.lon,
      coordinateSource: workerPhoto.coordinateSource,
      _binaryDataUrl: '', // Will be generated on demand
      _thumbnailData: workerPhoto.thumbnailData,
    }
  }

  /**
   * Clean up worker resources
   */
  cleanup() {
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
  }
}

// Global worker manager instance
let workerManager: PhotoWorkerManager | null = null

/**
 * Process photo file using Web Worker with ArrayBuffer (transferable)
 * This avoids duplicating large image data in memory during structured cloning.
 */
export async function processPhotoFromArrayBuffer(
  photoBuffer: ArrayBuffer,
  fileName: string,
  originalFile?: File,
  onProgress?: (progress: number, message?: string) => void,
): Promise<Photo | null> {
  try {
    const MAX_PHOTO_FILE_SIZE = 50 * 1024 * 1024 // 50MB limit
    if (photoBuffer.byteLength > MAX_PHOTO_FILE_SIZE) {
      throw new Error(
        `Photo file too large. Maximum size is ${MAX_PHOTO_FILE_SIZE / (1024 * 1024)}MB`,
      )
    }

    if (!workerManager) {
      workerManager = new PhotoWorkerManager()
    }

    const photo = await workerManager.processFileBuffer(photoBuffer, fileName, onProgress)

    // Store reference to original file if provided
    if (originalFile) {
      photo._originalFile = originalFile
    }

    return photo
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    showError(`Error processing photo: ${message}`)
    throw new Error(`Error processing photo: ${message}`)
  }
}
